import { Button, FuelIcon, Image, Title, Text, Field, TextField } from "components/commons";
import React, { useMemo } from "react";
import locale from "localization";
import { formatNumber, formatAmount, formatVolume, parseLiter } from "utils";
import styles from "./payment-success-page.module.scss";
import { prettifyProduct } from "utils/pretty.utils";
import { initialState as formState } from "./payment-or-details.state";
import { PaymentSuccess, Warning } from "images";
import classNames from "classnames";
import { useForm, useLeaveGuard, useModal, useMount } from "hooks";
import { NotifModal } from "components/modals";
import { PaymentMethod, InvoicingSetup } from "enums";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Big from "big.js";
const PaymentSuccessPage = ({
  loading,
  dispensedTo,
  literVolume,
  redemptionId,
  onSubmitOR,
  details,
  transactionDetails,
  setDetails,
  reset,
  initialState,
  dataFields,
  paymentId,
  checkCompanyGroup,
  // ...modal
}) => {
  const form = useMemo(() => {
    return formState(initialState);
  }, [initialState]);
  const { fields, modifyField, modifyForm, isFormSubmittable } = useForm({
    initialState: form,
  });

  const discount = dataFields?.totalDiscount.value;

  const subTotal = useMemo(() => {
    return parseFloat(
      parseLiter(dataFields?.actualAmountDispense.value || dataFields?.totalAmount.value),
    );
  }, [dataFields?.actualAmountDispense?.value, dataFields?.totalAmount.value]);

  const isFleetCardPath =
    details.paymentMethod === PaymentMethod.FleetCard ||
    details.paymentMethod === PaymentMethod.FleetCardManualPO ||
    details.paymentMethod === PaymentMethod.PLBLocqpayManualPOCredit;
  const receiptProductCode = isFleetCardPath ? dataFields?.productType?.value : details.productCode;

  const vatDivide = 1.12;
  let receiptVolume;
  let receiptUnitPrice;
  let receiptUnitAmount;
  let receiptDiscountPerUnit;
  let receiptDiscountPerTotal;
  let receiptPriceLOCQUnitPrice;
  let receiptVatableSales;
  let receiptVatAmount;
  let receiptTotalAmountDue;

  if (isFleetCardPath) {
    receiptVolume = dataFields?.computedVolume.value || 0;
    receiptUnitPrice = parseFloat(parseLiter(dataFields?.pumpPrice.value || 0)) || 0;
    receiptUnitAmount = subTotal || 0;
    receiptDiscountPerUnit = parseFloat(parseLiter(dataFields?.discountPerLiter?.value || 0)) || 0;
    receiptDiscountPerTotal = discount || 0;
    receiptTotalAmountDue = Number(Big(receiptUnitAmount || 0)) - (receiptDiscountPerTotal || 0);
    receiptVatableSales = receiptTotalAmountDue / vatDivide;
    receiptVatAmount = receiptTotalAmountDue - receiptVatableSales;
  } else {
    receiptVolume = literVolume || 0;
    receiptPriceLOCQUnitPrice = transactionDetails?.averageCreditPrice || 0;
    receiptUnitPrice =
      details?.pumpPrices?.find((item) => item.productCode === details.productCode)?.pumpPrice || 0;
    receiptUnitAmount = Number(Big(receiptUnitPrice).mul(receiptVolume));
    const priceLOCQAmount = Number(Big(receiptPriceLOCQUnitPrice).mul(receiptVolume));
    receiptDiscountPerUnit = Number(Big(receiptPriceLOCQUnitPrice)) - Number(Big(receiptUnitPrice));
    receiptDiscountPerTotal = Number(
      Big(priceLOCQAmount).round(2).minus(receiptUnitAmount).round(2),
    );
    receiptVatableSales = Number(Big(priceLOCQAmount).round(2).div(vatDivide).round(2));
    receiptVatAmount = Number(Big(priceLOCQAmount).minus(receiptVatableSales));
    receiptTotalAmountDue = Number(Big(receiptVatableSales).add(receiptVatAmount));
  }

  const isSingleInvoice =
    details?.fleet?.invoicingSetup === InvoicingSetup.SingleInvoicing && checkCompanyGroup;

  const leaveBlockedModal = useModal();
  useLeaveGuard({
    when: !isSingleInvoice,
    onBlocked: () =>
      leaveBlockedModal.show({
        title: locale.orNumberRequired,
        content: locale.orNumberRequiredBeforeLeaving,
        image: Warning,
      }),
  });

  useMount(() => {
    if (isSingleInvoice) {
      modifyForm({
        [fields.orNumber.name]: { validations: [], required: false },
      });
    }
  });

  return (
    <div className={styles.paymentSuccessContainer}>
      <div className={styles.confirmation}>
        <Text className={styles.headerPaymentSuccessTitle} strong>
          {locale.paymentSuccess}
        </Text>
        <div className={styles.titleContainer}>
          <Image src={PaymentSuccess} className={styles.successIcon} />
          <div className={styles.titleContent}>
            <Title medium className={styles.title}>
              {details.paymentMethod === PaymentMethod.FleetCard ||
              details.paymentMethod === PaymentMethod.FleetCardManualPO ||
              details.paymentMethod === PaymentMethod.PLBLocqpayManualPOCredit ? (
                <>{formatAmount(subTotal - discount)}</>
              ) : (
                <locale.Populate
                  text={literVolume === 1 ? locale.nLiter : locale.nLiters}
                  items={[formatNumber(literVolume)]}
                />
              )}
            </Title>
            <Text>
              <locale.Populate
                text={locale.ofText}
                className={styles.fuel}
                items={[
                  <span className={styles.fuel}>
                    <FuelIcon
                      className={styles.icon}
                      type={
                        details.paymentMethod === PaymentMethod.FleetCard ||
                        details.paymentMethod === PaymentMethod.FleetCardManualPO ||
                        details.paymentMethod === PaymentMethod.PLBLocqpayManualPOCredit
                          ? dataFields?.productType?.value
                          : details.productCode
                      }
                    />
                    <span className={styles.productFuel}>
                      {prettifyProduct(
                        details.paymentMethod === PaymentMethod.FleetCard ||
                          details.paymentMethod === PaymentMethod.FleetCardManualPO ||
                          details.paymentMethod === PaymentMethod.PLBLocqpayManualPOCredit
                          ? dataFields?.productType?.value
                          : details.productCode,
                      )}
                    </span>
                  </span>,
                ]}
              />
            </Text>
            <Text>{locale.hasBeenSuccessfullyRedeemed}</Text>
          </div>
        </div>
        <TableContainer component={Paper} elevation={0} className={styles.tableReceiptGuide}>
          <Table aria-label="receipt guide">
            <TableHead className={styles.tableHead}>
              <TableRow>
                <TableCell>{locale.volume}</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>{locale.productDescription}</TableCell>
                <TableCell>{locale.unitPrice}</TableCell>
                <TableCell>{locale.amount}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{formatVolume(receiptVolume)}</TableCell>
                <TableCell>{locale.liters}</TableCell>
                <TableCell>{prettifyProduct(receiptProductCode)}</TableCell>
                <TableCell align="right">{formatAmount(receiptUnitPrice)}</TableCell>
                <TableCell align="right">{formatAmount(receiptUnitAmount)}</TableCell>
              </TableRow>
              <TableRow className={styles.removeBorderButton}>
                <TableCell colSpan={2}></TableCell>
                <TableCell align="right">
                  <Text strong>{locale.discount}</Text>
                </TableCell>
                <TableCell align="right">{formatAmount(receiptDiscountPerUnit)}</TableCell>
                <TableCell align="right">{formatAmount(receiptDiscountPerTotal)}</TableCell>
              </TableRow>
              {!isFleetCardPath && (
                <TableRow>
                  <TableCell colSpan={2}></TableCell>
                  <TableCell align="right">
                    <Text strong>{locale.priceLOCQUnitPrice}</Text>
                  </TableCell>
                  <TableCell align="right">{formatAmount(receiptPriceLOCQUnitPrice)}</TableCell>
                  <TableCell align="right">
                    {formatAmount(Number(Big(receiptPriceLOCQUnitPrice).mul(receiptVolume)))}
                  </TableCell>
                </TableRow>
              )}
              <TableRow className={styles.removeBorderButton}>
                <TableCell colSpan={2} className={styles.vatSpaceFirst}></TableCell>
                <TableCell align="right" className={styles.vatSpaceFirst}>
                  {locale.vatTableSales}
                </TableCell>
                <TableCell className={styles.vatSpaceFirst}></TableCell>
                <TableCell align="right" className={styles.vatSpaceFirst}>
                  {formatAmount(receiptVatableSales)}
                </TableCell>
              </TableRow>
              <TableRow className={styles.removeBorderButton}>
                <TableCell colSpan={2} className={styles.vatSpace}></TableCell>
                <TableCell align="right" className={styles.vatSpace}>
                  {locale.vatExemptSales}
                </TableCell>
                <TableCell className={styles.vatSpace}></TableCell>
                <TableCell align="right" className={styles.vatSpace}>
                  {formatAmount(0)}
                </TableCell>
              </TableRow>
              <TableRow className={styles.removeBorderButton}>
                <TableCell colSpan={2} className={styles.vatSpace}></TableCell>
                <TableCell align="right" className={styles.vatSpace}>
                  {locale.vatZeroRatedSales}
                </TableCell>
                <TableCell className={styles.vatSpace}></TableCell>
                <TableCell align="right" className={styles.vatSpace}>
                  {formatAmount(0)}
                </TableCell>
              </TableRow>
              <TableRow className={styles.removeBorderButton}>
                <TableCell colSpan={2} className={styles.vatSpace}></TableCell>
                <TableCell align="right" className={styles.vatSpace}>
                  {locale.vatAmount}
                </TableCell>
                <TableCell className={styles.vatSpace}></TableCell>
                <TableCell align="right" className={styles.vatSpace}>
                  {formatAmount(receiptVatAmount)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2}></TableCell>
                <TableCell align="right">
                  <Text strong>{locale.totalAmountDue}</Text>
                </TableCell>
                <TableCell></TableCell>
                <TableCell align="right">
                  <Text strong>{formatAmount(receiptTotalAmountDue)}</Text>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <div className={styles.table}>
          <div className={styles.row}>
            <label>{locale.businessName}</label>
            <label>{details.fleet?.businessName || ""}</label>
          </div>
          <div className={styles.row}>
            <label>{locale.transactionId}</label>
            <label>
              {isFleetCardPath
                ? paymentId || locale.any
                : transactionDetails?.redemptionId || locale.any}
            </label>
          </div>
          <div className={styles.row}>
            <label>{locale.eligibleVehicle}</label>
            <label
              className={classNames({
                italic: !details.vehiclePlateNumber,
              })}
            >
              {details.vehiclePlateNumber || locale.any}
            </label>
          </div>
          {!isSingleInvoice && (
            <div className={styles.row}>
              <Field className={styles.fieldContainer} {...fields.orNumber}>
                <TextField
                  {...fields.orNumber}
                  className={styles.formContainer}
                  onChange={modifyField}
                />
              </Field>
            </div>
          )}
        </div>
        {isSingleInvoice && (
          <div className={styles.noteDoNotIssueReceiptSection}>
            <div className={styles.noteDoNotIssueReceipt} style={{ width: "100%" }}>
              <ErrorOutline className={styles.warningSign} />
              <div className={styles.warningTextSection}>
                <span className={styles.warningTextSingleInvoice}>
                  {locale.plbAccountIsInSingleInvoicing}
                </span>
                <span className={styles.warningTextDoNotIssue}>{locale.doNotIssueReceipt}</span>
              </div>
            </div>
          </div>
        )}
        <br></br>
        <Button
          primary
          className={styles.doneButton}
          disabled={!isSingleInvoice && !isFormSubmittable}
          loading={loading}
          onClick={() => {
            onSubmitOR(fields.orNumber);
            setDetails(null);
            reset();
          }}
        >
          {locale.done}
        </Button>
      </div>
      <NotifModal {...leaveBlockedModal} />
    </div>
  );
};

export default PaymentSuccessPage;
